<template>
    <div class="multi-input">
        <div v-for="i in modelValue.length + 1" :key="i">
            <div class="input-line">
                <Input
                    :ref="i"
                    class="input"
                    :model-value="modelValue[i - 1]"
                    :placeholder="isLastValue(i - 1) ? newValuePlaceholder : ''"
                    @onChange="onChange($event.target.value, i - 1)"
                    @onBlur="removeValueIAtIndexEmpty(i - 1)"
                    @keydown="keyupHandler($event, i - 1)"
                />

                <Button
                    v-if="modelValue.length && i - 1 !== modelValue.length"
                    class="remove-button"
                    icon="ios-close"
                    shape="circle"
                    size="small"
                    tabindex="-1"
                    @click="removeValueAtIndex(i - 1)"
                />
            </div>

            <template v-if="errorsByIndex[i - 1] && showErrors">
                <p v-for="errorMessage in errorsByIndex[i - 1]" :key="errorMessage" class="error-message">
                    {{ errorMessage }}
                </p>
            </template>
        </div>

        <div v-if="error && showErrors">
            <p v-for="errorMessage in error" :key="errorMessage" class="error-message">
                {{ errorMessage }}
            </p>
        </div>
    </div>
</template>

<script lang="ts">
import type { PropType } from "vue";

const tabKeyCode = 9;

export default {
    props: {
        error: {
            type: Array as PropType<string[]>
        },
        errorsByIndex: {
            type: Object as PropType<Record<number, string[]>>,
            default: () => ({})
        },
        newValuePlaceholder: {
            type: String,
            default: "Type here to add an item"
        },
        showErrors: {
            type: Boolean
        },
        modelValue: {
            type: Array as PropType<string[]>,
            default: () => []
        }
    },

    emits: ["update:modelValue", "inputRemoved", "inputBlur"],

    data() {
        return {
            inputToFocus: null as number | null
        };
    },

    updated() {
        if (this.inputToFocus !== null) {
            (this.$refs as Record<number, HTMLInputElement[]>)[this.inputToFocus][0].focus();
            this.inputToFocus = null;
        }
    },

    methods: {
        isLastValue(index: number) {
            return this.modelValue.length - 1 === index;
        },

        async keyupHandler(event: KeyboardEvent, index: number) {
            const currentValue = this.modelValue[index];

            if (event.keyCode === tabKeyCode && !event.shiftKey && !this.isLastValue(index) && !currentValue) {
                /*
                    If we have tabbed with a value that is empty then we need
                    to do a little dance to make the next input be the input
                    that we are focused on.

                    Without doing this we will jump over the next input due to
                    how we are keying the values in the template.
                */
                this.inputToFocus = index;
            }
        },

        onChange(value: string, index: number) {
            const newValue = [...this.modelValue];
            newValue[index] = value;

            this.$emit("update:modelValue", newValue);
        },

        removeValueAtIndex(index: number) {
            const newValue = [...this.modelValue];
            newValue.splice(index, 1);

            this.$emit("update:modelValue", newValue);
            this.$emit("inputRemoved", index);
        },

        removeValueIAtIndexEmpty(index: number) {
            const value = this.modelValue[index];

            if (this.modelValue.length > 1 && (value === null || value === undefined || value === "")) {
                this.removeValueAtIndex(index);
            }

            this.$emit("inputBlur", index);
        }
    }
};
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;
.multi-input {
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: $spacing-small;
}

.input-line {
    display: flex;
    gap: $spacing-small;
    align-items: center;
}

.input {
    flex: 0 calc(100% - 32px);
}

.remove-button {
    flex: 0 0 auto;
}

.error-message {
    padding-top: 4px;
    color: $error-color;
    font-size: $font-size-small;
}
</style>
